import AppPaths from '@globals/paths'
import { colors } from '@globals/themes/defaultTheme'
import { MemberEvents } from '@kudos/http-client'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import {
  ADORNMENT_BG_COLOR,
  ADORNMENT_ICON,
  PREVIEW_CELEBRATION_ADORNMENT,
} from '@shared/avatar/userAvatarAdornmentUtils'
import { T } from '@transifex/react'
import { cloneElement, type ReactElement } from 'react'
import { Link as RouterLink } from 'react-router'
import { getActiveCelebrationBanners } from './celebrationBannerUtils'
import { MemberCelebration } from './memberEvents'

const bannerSx = (bgColor: string) => ({
  borderRadius: 2,
  px: 1.5,
  py: 1,
  backgroundColor: bgColor,
})

type CelebrationBannersProps = {
  events?: MemberEvents | null
  /** Used to address the member in the banner, e.g. "Farah's birthday is June 29!". */
  firstName: string
  /** Greets the member instead of naming them, for their own profile. */
  isOwnProfile?: boolean
  /** Sign-card ids keyed by celebration; adds a "Sign card" link to matching banners. */
  signCards?: Partial<Record<MemberCelebration, string>> | null
  /**
   * Where the "Sign card" link sits: beside the message, or under it where the
   * banner is too narrow to fit both on one line, as in the profile card.
   */
  signCardPlacement?: 'inline' | 'below'
}

const OWN_PROFILE_GREETING: Record<MemberCelebration, React.ReactNode> = {
  [MemberCelebration.BIRTHDAY]: <T _str="Happy birthday!" />,
  [MemberCelebration.ANNIVERSARY]: <T _str="Happy anniversary!" />,
  [MemberCelebration.WELCOME]: <T _str="Welcome!" />,
}

/**
 * Renders one banner per celebration the member is currently in the highlight
 * window for, respecting their privacy settings. Carries the celebratory
 * treatment on the profile card and, on mobile where there is no card, above
 * the profile tabs.
 */
const CelebrationBanners: React.FC<CelebrationBannersProps> = ({
  events,
  firstName,
  isOwnProfile = false,
  signCards,
  signCardPlacement = 'inline',
}) => {
  const banners = getActiveCelebrationBanners(events, firstName)

  if (banners.length === 0) return null

  return (
    <Stack useFlexGap spacing={1}>
      {banners.map(({ type, milestone, label, bgColor }) => {
        const adornment = PREVIEW_CELEBRATION_ADORNMENT[type]
        const cardId = isOwnProfile ? undefined : signCards?.[milestone]
        const signCardLink = !!cardId && (
          <Link
            component={RouterLink}
            to={AppPaths.tenant.viewCard(cardId)}
            sx={{ color: colors.bluePrimary, whiteSpace: 'nowrap' }}>
            <span>
              <T _str="Sign card" />
            </span>
          </Link>
        )
        return (
          <Stack
            key={type}
            direction="row"
            useFlexGap
            spacing={1}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              ...bannerSx(bgColor),
            }}>
            <Stack
              direction="row"
              useFlexGap
              spacing={1}
              sx={{
                alignItems: 'center',
                minWidth: 0,
              }}>
              <Box component="span" aria-hidden sx={{ display: 'inline-flex', lineHeight: 0 }}>
                {cloneElement(
                  ADORNMENT_ICON[adornment] as ReactElement<{
                    fontSize?: 'medium' | 'small' | 'large' | 'inherit'
                    sx?: SxProps
                  }>,
                  {
                    fontSize: 'medium',
                    sx: { color: ADORNMENT_BG_COLOR[adornment] },
                  },
                )}
              </Box>
              <Stack
                useFlexGap
                spacing={0.25}
                sx={{
                  minWidth: 0,
                  alignItems: 'flex-start',
                }}>
                <Typography variant="body1" sx={{ color: colors.grayMedium }}>
                  {isOwnProfile ? OWN_PROFILE_GREETING[milestone] : label}
                </Typography>
                {signCardPlacement === 'below' && signCardLink}
              </Stack>
            </Stack>
            {signCardPlacement === 'inline' && signCardLink}
          </Stack>
        )
      })}
    </Stack>
  )
}

export default CelebrationBanners
